import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

// --- UTM capture helpers (isolated — never block self-register) ---
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];

function getOrCreateMktSession() {
  try {
    let sid = localStorage.getItem("mkt_sid");
    if (!sid) {
      sid = "s" + Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem("mkt_sid", sid);
    }
    return sid;
  } catch { return null; }
}

function getUtmFromUrl() {
  try {
    const u = new URL(window.location.href);
    const out = {};
    UTM_KEYS.forEach((k) => { const v = u.searchParams.get(k); if (v) out[k] = v; });
    const stored = (() => { try { return JSON.parse(localStorage.getItem("mkt_utm") || "{}"); } catch { return {}; } })();
    const merged = { ...stored, ...out };
    if (Object.keys(out).length) {
      try { localStorage.setItem("mkt_utm", JSON.stringify(merged)); } catch {}
    }
    return merged;
  } catch { return {}; }
}

async function trackMarketing(event, extra = {}) {
  try {
    const session_id = getOrCreateMktSession();
    if (!session_id) return;
    const utm = getUtmFromUrl();
    await api.post("/marketing/track", {
      event, session_id,
      landing_path: window.location.pathname + window.location.search,
      referrer: document.referrer || null,
      ...utm,
      ...extra,
    });
  } catch { /* never break self-register on tracking errors */ }
}

// Bilingual label helper: English on top, Spanish small below
function BiLabel({ en, es, required = false, htmlFor }) {
  return (
    <Label htmlFor={htmlFor} className="block">
      <span className="font-semibold text-slate-800 dark:text-slate-100">{en}{required && <span className="text-red-500 ml-0.5">*</span>}</span>
      <span className="block text-[11px] text-slate-500 mt-0.5 italic">{es}</span>
    </Label>
  );
}

const empty = {
  tour_id: "",
  first_name: "", last_name: "", email: "", phone: "", country: "",
  gender: "", birthdate: "",
  passport_number: "", passport_expiry: "",
  emergency_contact: "", emergency_contact_phone: "",
  dietary: "", allergies: "", medical: "",
  tshirt_size: "",
};

export default function SelfRegister() {
  const [tours, setTours] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(null);
  const honeypotRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/public/tours");
        setTours(data);
      } catch { setTours([]); }
    })();
    // Fire a pageview tracking event — isolated, never blocks the form.
    trackMarketing("pageview");
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const submit = async () => {
    if (!form.tour_id) return toast.error("Please choose an expedition / Elige una expedición");
    if (!form.first_name.trim() || !form.last_name.trim()) return toast.error("Name required / Nombre obligatorio");
    if (!form.email.trim() || !form.email.includes("@")) return toast.error("Valid email required / Email válido");
    setSaving(true);
    try {
      const website = honeypotRef.current?.value || "";
      const { data } = await api.post("/public/self-register", { ...form, website });
      setDone(data);
      toast.success("Registration sent / Inscripción enviada");
      // Fire a submit tracking event AFTER success — never block the main flow.
      trackMarketing("submit", { email: form.email, tour_id: form.tour_id });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-slate-50 dark:from-[#0B1220] dark:via-[#0F1729] dark:to-[#0B1220] flex items-center justify-center p-6">
        <Card className="max-w-lg w-full border-emerald-200 dark:border-emerald-900">
          <CardContent className="p-10 text-center">
            <CheckCircle2 size={48} className="text-emerald-500 mx-auto" />
            <h1 className="text-2xl font-bold mt-4">Thank you!</h1>
            <p className="text-sm text-slate-500 italic mt-0.5">¡Gracias!</p>
            <p className="text-slate-700 dark:text-slate-200 mt-4">
              Thank you so much for signing up to <b>{done.tour_name}</b>. We hope you have an amazing trip!
            </p>
            <p className="text-xs text-slate-500 italic mt-2">
              Muchas gracias por apuntarte a <b>{done.tour_name}</b>. ¡Esperamos que disfrutes muchísimo del viaje!
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-slate-50 dark:from-[#0B1220] dark:via-[#0F1729] dark:to-[#0B1220] py-10 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <img
            src="https://customer-assets-4nw71qhi.emergentagent.net/job_travel-ops-central/artifacts/jrl28zc2_logo%20fondo%20blanco%20solo%20letras.png"
            alt="IceMonkey Photo Expeditions"
            className="h-12 w-auto mx-auto dark:invert"
          />
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-6">Expedition sign-up</h1>
          <p className="text-sm text-slate-500 italic mt-1">Inscripción a una expedición</p>
          <p className="text-slate-600 dark:text-slate-300 max-w-xl mx-auto mt-3 text-sm">
            Fill in your details to join one of our photography expeditions. All fields are optional except name, email and expedition.
          </p>
          <p className="text-xs text-slate-500 italic mt-1">
            Rellena tus datos para apuntarte a una expedición. Solo son obligatorios nombre, email y expedición.
          </p>
        </div>

        <Card>
          <CardContent className="p-6 sm:p-8 space-y-6">
            {/* Tour selector */}
            <div>
              <BiLabel en="Expedition" es="Expedición" required />
              <Select value={form.tour_id} onValueChange={(v) => setForm({ ...form, tour_id: v })}>
                <SelectTrigger data-testid="self-tour" className="mt-2"><SelectValue placeholder="Choose expedition / Elige expedición…" /></SelectTrigger>
                <SelectContent>
                  {(tours || []).map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name} — {t.country || "—"} {t.start_date ? `· ${t.start_date}` : ""}
                    </SelectItem>
                  ))}
                  {tours && tours.length === 0 && <div className="px-3 py-2 text-sm text-slate-500">No expeditions available.</div>}
                </SelectContent>
              </Select>
            </div>

            {/* Personal */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><BiLabel en="First name" es="Nombre" required /><Input data-testid="self-first" value={form.first_name} onChange={set("first_name")} className="mt-2" /></div>
              <div><BiLabel en="Last name" es="Apellidos" required /><Input data-testid="self-last" value={form.last_name} onChange={set("last_name")} className="mt-2" /></div>
              <div><BiLabel en="Email" es="Correo electrónico" required /><Input data-testid="self-email" type="email" value={form.email} onChange={set("email")} className="mt-2" /></div>
              <div><BiLabel en="Phone" es="Teléfono" /><Input value={form.phone} onChange={set("phone")} className="mt-2" placeholder="+34 600 000 000" /></div>
              <div><BiLabel en="Country" es="País de residencia" /><Input value={form.country} onChange={set("country")} className="mt-2" /></div>
              <div>
                <BiLabel en="Gender" es="Género" />
                <Select value={form.gender || "__none__"} onValueChange={(v) => setForm({ ...form, gender: v === "__none__" ? "" : v })}>
                  <SelectTrigger className="mt-2"><SelectValue placeholder="Select / Elegir" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Prefer not to say / Prefiero no decirlo —</SelectItem>
                    <SelectItem value="male">Male / Hombre</SelectItem>
                    <SelectItem value="female">Female / Mujer</SelectItem>
                    <SelectItem value="other">Other / Otro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><BiLabel en="Date of birth" es="Fecha de nacimiento" /><Input type="date" value={form.birthdate} onChange={set("birthdate")} className="mt-2" /></div>
              <div><BiLabel en="T-shirt size" es="Talla de camiseta" />
                <Select value={form.tshirt_size || "__none__"} onValueChange={(v) => setForm({ ...form, tshirt_size: v === "__none__" ? "" : v })}>
                  <SelectTrigger className="mt-2"><SelectValue placeholder="Select / Elegir" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">—</SelectItem>
                    {["XS","S","M","L","XL","XXL"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Passport */}
            <div>
              <h3 className="font-semibold text-sm text-[#026FCB] uppercase tracking-wider">Passport</h3>
              <p className="text-[11px] text-slate-500 italic">Pasaporte</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
                <div><BiLabel en="Passport number" es="Número de pasaporte" /><Input value={form.passport_number} onChange={set("passport_number")} className="mt-2" /></div>
                <div><BiLabel en="Expiry date" es="Fecha de caducidad" /><Input type="date" value={form.passport_expiry} onChange={set("passport_expiry")} className="mt-2" /></div>
              </div>
            </div>

            {/* Emergency contact */}
            <div>
              <h3 className="font-semibold text-sm text-[#026FCB] uppercase tracking-wider">Emergency contact</h3>
              <p className="text-[11px] text-slate-500 italic">Contacto de emergencia</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
                <div><BiLabel en="Contact name & relationship" es="Nombre y parentesco" /><Input value={form.emergency_contact} onChange={set("emergency_contact")} className="mt-2" placeholder="Jane Smith — spouse" /></div>
                <div><BiLabel en="Contact phone" es="Teléfono de contacto" /><Input value={form.emergency_contact_phone} onChange={set("emergency_contact_phone")} className="mt-2" placeholder="+34 600 000 000" /></div>
              </div>
            </div>

            {/* Health */}
            <div>
              <h3 className="font-semibold text-sm text-[#026FCB] uppercase tracking-wider">Health &amp; dietary</h3>
              <p className="text-[11px] text-slate-500 italic">Salud y dieta</p>
              <div className="space-y-3 mt-3">
                <div><BiLabel en="Diet (vegetarian, vegan, halal…)" es="Dieta (vegetariana, vegana, halal…)" /><Input value={form.dietary} onChange={set("dietary")} className="mt-2" /></div>
                <div><BiLabel en="Allergies" es="Alergias" /><Input value={form.allergies} onChange={set("allergies")} className="mt-2" /></div>
                <div><BiLabel en="Medical conditions or medications" es="Condiciones médicas o medicaciones" /><Input value={form.medical} onChange={set("medical")} className="mt-2" /></div>
              </div>
            </div>

            <div className="pt-4 border-t border-slate-200 dark:border-[#25334D] flex items-center justify-end">
              {/* Honeypot — bots fill this, humans never see it */}
              <input
                ref={honeypotRef}
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
              />
              <Button data-testid="self-submit" onClick={submit} disabled={saving} className="bg-[#026FCB] hover:bg-[#0358a5] px-8">
                {saving ? "Sending… / Enviando…" : "Send registration / Enviar inscripción"}
              </Button>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-slate-500 mt-6">
          www.icemonkeytours.com — Your data is kept private and used only for expedition logistics.
        </p>
      </div>
    </div>
  );
}
